/**
 * OperationsTasks.tsx — Operations / Field Schedule (M5a list + M5b
 * calendar).
 *
 * M5b makes the CALENDAR the primary view (the mockup's Schedules screen,
 * klaxiora-crm-mockup-full.html :1840-1921) with a Calendar/List toggle —
 * the M5a day-grouped list survives as the List tab. The month grid is
 * in-house (calendarMath.ts; the mockup hardcodes one July-2026 grid,
 * :1852-1908) with a WORKING ‹ › month nav + Today jump (the mockup's nav
 * is inert), fetched through the existing windowed list for the visible
 * grid range — other-month edge cells carry real pins.
 *
 * Drag = DAY-precision pin move only (the mockup's real interaction,
 * :3604-3631), native HTML5 dnd per the SalesPipeline precedent: a drop
 * rides the EXISTING full-replace PUT with only the date changed — no
 * backend contact. A COMPLETED task is not draggable: a record doesn't
 * reschedule (the same honesty as the delete fence). Pin click opens the
 * mockup's Task Detail modal (:2675-2708) — Delete / Mark Complete (our
 * honest extension; the mockup has no complete UI) / Close / Edit, the
 * Edit handing off to the M5a drawer unchanged.
 *
 * Team Tracker (mockup :1911-1920) renders under the calendar, scoped to
 * the DISPLAYED month so its month claim is true (tasksView.teamTracker;
 * labels follow M6's honesty naming — "Team with tasks", not agents).
 * The mockup's renderAgentProfiles (:3830-3838) has NO real equivalent
 * and is deliberately NOT built: the real app has no field-agent profile
 * page — per-person counts live only in the tracker.
 *
 * P1: colors/sizes flow from src/theme/tokens.ts (canonical P1 design system).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import { SectionTitle, LoadingState, ErrorState, EmptyState } from '../../DashboardShared'
import { OpsMetric } from './OperationsOverview'
import { useAsync, fetchTasks, createTask, updateTask, completeTask, deleteTask, fetchDirectory } from '../../api'
import { apiErrorMessage } from '../../../../api/client'
import { overlay, success, accent } from '../../../../theme/tokens'
import type { OpsTask, TaskWrite } from '../../types'
import { TASK_TYPES, taskTypeLabel, taskCountLine, formatTaskDay, groupTasksByDate, teamTracker, trackerInitials } from './tasksView'
import { formatSopDate } from './sopsView'
import { monthGrid, monthOf, prevMonth, nextMonth } from './calendarMath'
import { useWriteAccess } from '../../../../lib/writeAccess'

// ── Create / Edit drawer (the AssetDrawer pattern — full-replace PUT) ────────

function TaskDrawer({ task, onClose, onSaved }: {
  task: OpsTask | null // null = create
  onClose: () => void
  onSaved: () => void
}) {
  const [taskType, setTaskType] = useState(task?.taskType ?? 'client_visit')
  const [assignedTo, setAssignedTo] = useState(task?.assignedTo ?? '')
  const [scheduledDate, setScheduledDate] = useState(task?.scheduledDate ?? '')
  const [scheduledTime, setScheduledTime] = useState(task?.scheduledTime ?? '')
  const [notes, setNotes] = useState(task?.notes ?? '')

  // Assignee picklist = live ACTIVE employees with an Unassigned blank —
  // the M1-M4 picker pattern (people only, decision M5a).
  const employees = useAsync(() => fetchDirectory({ status: 'active' }), [])

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function handleSave() {
    if (!scheduledDate) { toast.error('Date is required'); return }
    // Full-replace write: an empty assignee/time is OMITTED, which the
    // server nulls — unassign and clear-time (decision M5a); a changed
    // date IS the reschedule.
    const write = {
      taskType, scheduledDate, notes,
      assignedTo: assignedTo || undefined,
      scheduledTime: scheduledTime || undefined,
    }
    try {
      if (task) {
        await updateTask(task.id, write)
        toast.success('Task updated')
      } else {
        await createTask(write)
        toast.success('Task scheduled')
      }
      onSaved()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, task ? 'Failed to update task' : 'Failed to schedule task'))
    }
  }

  async function handleDelete() {
    if (!task) return
    try {
      await deleteTask(task.id)
      toast.success('Task deleted')
      onSaved()
      onClose()
    } catch (e) {
      // The completed-task fence lands here as the server's named 400.
      toast.error(apiErrorMessage(e, 'Failed to delete task'))
    }
  }

  // bg-surface-control + ink-body is the PROVEN portal-form recipe (the
  // PR-259/261 sweep): a portal renders outside .dash-shell, so without an
  // explicit background token the UA paints inputs white and the light ink
  // tokens vanish into them.
  const inputCls = 'w-full px-4 py-3 bg-surface-control border border-edge-control rounded-control text-13 text-ink-body placeholder:text-ink-muted transition-all'
  const labelCls = 'block text-12 font-semibold text-ink-body mb-2'

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      {/* PORTAL RULE (theme/theme.ts): no colorScheme here. data-theme +
          color-scheme are stamped on <html>, so this portal subtree inherits
          both — UA-drawn select popups and picker chrome follow the ACTIVE
          theme instead of a hardcoded 'dark' that would invert in light mode. */}
      <div className="fixed top-0 right-0 bg-surface-card border-l border-edge-row flex flex-col"
        style={{ height: '100dvh', width: 'min(580px, 100vw)', animation: 'slide-in-right 220ms cubic-bezier(0.23,1,0.32,1)' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{task ? 'Edit Task' : 'New Task'}</div>
            <div className="text-12 text-ink-muted mt-1">
              {task ? 'Reschedule it, reassign it, or correct the details.' : 'Schedule a field task — a visit, a demo, a signing.'}
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Form */}
        <div className="flex-1 overflow-y-auto px-6 py-5 flex flex-col gap-5">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Task</label>
              <select value={taskType} onChange={e => setTaskType(e.target.value)} autoFocus className={inputCls}>
                {TASK_TYPES.map(t => <option key={t.slug} value={t.slug}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Assigned to</label>
              {employees.error ? (
                <div className="text-12 text-ink-muted px-1 py-3">Couldn't load employees</div>
              ) : (
                <select value={assignedTo} onChange={e => setAssignedTo(e.target.value)}
                  disabled={employees.loading} className={inputCls}>
                  <option value="">Unassigned</option>
                  {(employees.data ?? []).map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.fullName}</option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Date<span className="text-danger ml-1">*</span></label>
              <input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Time</label>
              <input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)}
                className={inputCls} />
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Anything the assignee needs to know..." rows={5}
              className={`${inputCls} resize-none`} />
          </div>
        </div>

        {/* Footer — Delete sits left in edit mode (the mockup's Delete Task
            slot); the server's completed-task fence answers it with a named
            400 that apiErrorMessage surfaces. */}
        <div className="flex items-center gap-2 px-6 pt-5 pb-14 border-t border-edge-row flex-shrink-0">
          {task && (
            <button onClick={handleDelete}
              className="k-btn-secondary text-danger mr-auto">
              Delete Task
            </button>
          )}
          <button onClick={onClose}
            className="k-btn-secondary ml-auto">
            Cancel
          </button>
          <button onClick={handleSave}
            className="k-btn">
            {task ? 'Save Changes' : 'Add Task'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Task Detail modal (M5b — the mockup's :2675-2708, swept portal recipe) ───

function TaskDetailModal({ task, onClose, onEdit, onChanged }: {
  task: OpsTask
  onClose: () => void
  onEdit: (task: OpsTask) => void
  onChanged: () => void
}) {
  // Delete / complete / edit are records-level writes (lib/writeAccess):
  // without access the modal is the detail and Close.
  const { records } = useWriteAccess()
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [onClose])

  async function handleComplete() {
    try {
      await completeTask(task.id)
      toast.success('Task completed')
      onChanged()
      onClose()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to complete task'))
    }
  }

  async function handleDelete() {
    try {
      await deleteTask(task.id)
      toast.success('Task deleted')
      onChanged()
      onClose()
    } catch (e) {
      // The completed-task fence lands here as the server's named 400.
      toast.error(apiErrorMessage(e, 'Failed to delete task'))
    }
  }

  const row = (label: string, value: string, muted = false) => (
    <div className="flex items-start justify-between gap-6 py-3 border-b border-edge-row-faint last:border-0">
      <div className="text-12 font-semibold text-ink-muted flex-shrink-0">{label}</div>
      <div className={`text-13 text-right ${muted ? 'text-ink-muted' : 'text-ink-body'}`}>{value}</div>
    </div>
  )

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: overlay.scrim, backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      {/* PORTAL RULE (theme/theme.ts): no colorScheme here. data-theme +
          color-scheme are stamped on <html>, so this portal subtree inherits
          both — UA-drawn select popups and picker chrome follow the ACTIVE
          theme instead of a hardcoded 'dark' that would invert in light mode. */}
      <div role="dialog" aria-modal="true" aria-label={`Task detail: ${taskTypeLabel(task.taskType)}`}
        className="k-card"
        style={{ width: 'min(440px, calc(100vw - 32px))' }}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-edge-row">
          <div>
            <div className="text-detail-title font-bold text-ink-primary">{taskTypeLabel(task.taskType)}</div>
            <div className="text-12 text-ink-muted mt-1">{formatSopDate(task.scheduledDate)}</div>
          </div>
          <button onClick={onClose} aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-control text-ink-muted hover:bg-surface-nav-hover transition-colors">
            <X size={16} />
          </button>
        </div>

        {/* Detail rows — the mockup's field set (:2675-2708) */}
        <div className="px-6 py-2">
          {row('Status', task.status === 'completed' ? 'Completed' : 'Scheduled')}
          {row('Assigned to', task.assignedToName || 'Unassigned', !task.assignedToName)}
          {row('Date', formatSopDate(task.scheduledDate))}
          {row('Time', task.scheduledTime || 'No time set', !task.scheduledTime)}
          {row('Notes', task.notes || 'No notes added', !task.notes)}
        </div>

        {/* Actions: the mockup's Delete/Close/Edit plus Mark Complete — the
            honest extension (the mockup has no complete UI at all). */}
        <div className="flex items-center gap-2 px-6 pt-4 pb-5 border-t border-edge-row">
          {records ? (
            <button onClick={handleDelete}
              className="k-btn-secondary text-danger mr-auto">
              Delete Task
            </button>
          ) : <span className="mr-auto" />}
          {task.status === 'scheduled' && records && (
            <button className="k-btn-secondary" onClick={handleComplete}>Mark Complete</button>
          )}
          <button className="k-btn-secondary" onClick={onClose}>Close</button>
          {records && <button className="k-btn" onClick={() => onEdit(task)}>Edit</button>}
        </div>
      </div>
    </div>,
    document.body
  )
}

// ── Shared bits ───────────────────────────────────────────────────────────────

function TaskStatusBadge({ status }: { status: string }) {
  if (status === 'completed') {
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium"
        style={{ background: success.bg, color: success.text }}>
        Completed
      </span>
    )
  }
  return (
    <span className="inline-flex items-center px-2 py-1 rounded-full text-11 font-medium bg-surface-panel text-ink-secondary">
      Scheduled
    </span>
  )
}

// ── Calendar view (M5b primary) ───────────────────────────────────────────────

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function CalendarView({ reloadKey, onReload, action, onOpenDetail }: {
  reloadKey: number
  onReload: () => void
  action: ReactNode
  onOpenDetail: (task: OpsTask) => void
}) {
  const [{ year, month }, setMonth] = useState(() => monthOf(new Date()))
  const draggingId = useRef<string | null>(null)
  const [dragOver, setDragOver] = useState<string | null>(null)
  // A drop reschedules (a task update): records-level (lib/writeAccess).
  const { records } = useWriteAccess()

  const grid = monthGrid(year, month, new Date())
  const { data, loading, error } = useAsync(
    () => fetchTasks({ from: grid.gridStart, to: grid.gridEnd }),
    [year, month, reloadKey],
  )

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const tasks = data ?? []
  const byDate = new Map<string, OpsTask[]>()
  for (const t of tasks) {
    byDate.set(t.scheduledDate, [...(byDate.get(t.scheduledDate) ?? []), t])
  }
  // Tracker scope = the DISPLAYED month only, so the card's month claim is
  // true (the mockup computes over everything under a "this month" label).
  const monthPrefix = `${year}-${String(month).padStart(2, '0')}`
  const tracker = teamTracker(tasks.filter(t => t.scheduledDate.startsWith(monthPrefix)))

  async function handleDrop(date: string) {
    if (!records) return
    const id = draggingId.current
    draggingId.current = null
    setDragOver(null)
    if (!id) return
    const task = tasks.find(t => t.id === id)
    if (!task || task.scheduledDate === date) return
    // Reschedule = the existing full-replace PUT with ONLY the date
    // changed (decision M5b) — every other field round-trips as-is.
    const write: TaskWrite = {
      taskType: task.taskType,
      scheduledDate: date,
      notes: task.notes ?? '',
      assignedTo: task.assignedTo || undefined,
      scheduledTime: task.scheduledTime || undefined,
    }
    try {
      await updateTask(id, write)
      toast.success(`Moved to ${formatSopDate(date)}`)
      onReload()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to move task'))
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Field Schedule"
        subtitle={tasks.length === 0 ? 'The month at a glance — drag a task to reschedule it.' : `${tasks.length} task${tasks.length !== 1 ? 's' : ''} on the ${grid.label} grid — drag to reschedule.`}
        action={action}
      />

      <div className="k-card">
        {/* Toolbar: WORKING month nav + Today (the mockup's ‹› is inert). */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-edge-row">
          <div className="text-16 font-bold text-ink-primary">{grid.label}</div>
          <div className="flex items-center gap-2">
            <button aria-label="Previous month" onClick={() => setMonth(prevMonth(year, month))}
              className="w-8 h-8 flex items-center justify-center rounded-control border border-edge-control text-ink-secondary hover:bg-surface-row-hover transition-colors">
              <ChevronLeft size={15} />
            </button>
            <button aria-label="Next month" onClick={() => setMonth(nextMonth(year, month))}
              className="w-8 h-8 flex items-center justify-center rounded-control border border-edge-control text-ink-secondary hover:bg-surface-row-hover transition-colors">
              <ChevronRight size={15} />
            </button>
            <button className="k-btn-secondary" onClick={() => setMonth(monthOf(new Date()))}>Today</button>
          </div>
        </div>

        <div className="grid grid-cols-7">
          {DAY_LABELS.map(d => (
            <div key={d} className="px-2 py-2 text-11 font-semibold uppercase text-ink-muted text-center border-b border-edge-row-faint">
              {d}
            </div>
          ))}
          {grid.cells.map(cell => (
            <div key={cell.date} data-date={cell.date}
              onDragOver={e => { e.preventDefault(); if (dragOver !== cell.date) setDragOver(cell.date) }}
              onDragLeave={() => { if (dragOver === cell.date) setDragOver(null) }}
              onDrop={e => { e.preventDefault(); void handleDrop(cell.date) }}
              className={`min-h-[96px] p-1.5 border-b border-r border-edge-row-faint flex flex-col gap-1 transition-colors ${dragOver === cell.date ? 'bg-surface-panel' : ''}`}>
              <div className="flex items-center justify-between">
                {cell.isToday ? (
                  <span className="w-6 h-6 flex items-center justify-center rounded-full bg-accent text-ink-on-accent text-11 font-bold">{cell.day}</span>
                ) : (
                  <span className={`text-11 font-medium px-1 ${cell.inMonth ? 'text-ink-secondary' : 'text-ink-muted'}`}>{cell.day}</span>
                )}
              </div>
              {(byDate.get(cell.date) ?? []).map(t => {
                const completed = t.status === 'completed'
                return (
                  <div key={t.id}
                    draggable={!completed && records}
                    onDragStart={() => { draggingId.current = t.id }}
                    onDragEnd={() => { draggingId.current = null; setDragOver(null) }}
                    onClick={() => onOpenDetail(t)}
                    title={`${taskTypeLabel(t.taskType)}${t.assignedToName ? ` · ${t.assignedToName}` : ''}`}
                    className={`text-11 rounded-control px-1.5 py-1 truncate border transition-shadow ${completed
                      ? 'border-transparent cursor-pointer'
                      : 'bg-surface-control text-ink-body border-edge-control cursor-grab active:cursor-grabbing hover:shadow-deal'}`}
                    style={completed ? { background: success.bg, color: success.text } : undefined}>
                    {t.scheduledTime && <span className="font-semibold mr-1">{t.scheduledTime}</span>}
                    {taskTypeLabel(t.taskType)}
                    {t.assignedToName ? ` · ${t.assignedToName}` : ''}
                  </div>
                )
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Team Tracker (mockup :1911-1920) — month-scoped, honest labels. */}
      <div className="k-card">
        <div className="k-card-head"><div><div className="k-card-title text-ink-primary">Team Tracker</div><div className="k-card-sub">{`Workload across ${grid.label}`}</div></div></div>
        <div className="p-5 flex flex-col gap-4">
          <div className="grid grid-cols-4 gap-3">
            <OpsMetric label="Total tasks" value={tracker.total} note={grid.label} />
            <OpsMetric label="Completed" value={tracker.completed} note="Done and recorded" />
            <OpsMetric label="Scheduled" value={tracker.scheduled} note="Still to be completed" />
            <OpsMetric label="Team with tasks" value={tracker.team} note="People with at least one task" />
          </div>
          {tracker.rows.length === 0 ? (
            <EmptyState message="No tasks assigned yet" hint={records ? 'Assign a task and workload will show up here' : 'Workload will show up here once tasks are assigned'} />
          ) : (
            <div className="flex flex-col gap-3">
              {tracker.rows.map(r => (
                <div key={r.name} className="flex items-center gap-3">
                  <span className="w-8 h-8 flex items-center justify-center rounded-full text-11 font-bold flex-shrink-0"
                    style={{ background: accent.soft, color: accent.DEFAULT }}>
                    {trackerInitials(r.name)}
                  </span>
                  <div className="text-13 text-ink-primary font-medium w-40 flex-shrink-0 truncate">{r.name}</div>
                  <div className="flex-1 h-2 rounded-full bg-surface-panel overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${r.share}%`, background: accent.DEFAULT }} />
                  </div>
                  <div className="text-12 text-ink-secondary flex-shrink-0 w-44 text-right">
                    {r.total} task{r.total !== 1 ? 's' : ''} · {r.completed} completed
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ── List view (the M5a interim surface, retained as the List tab) ─────────────

function ListView({ reloadKey, onReload, action, onEdit }: {
  reloadKey: number
  onReload: () => void
  action: ReactNode
  onEdit: (task: OpsTask) => void
}) {
  const { data, loading, error } = useAsync(() => fetchTasks(), [reloadKey])
  // Complete and Edit are records-level writes (lib/writeAccess).
  const { records } = useWriteAccess()

  if (loading) return <LoadingState />
  if (error) return <ErrorState message={error} />

  const tasks = data ?? []
  const groups = groupTasksByDate(tasks)

  async function handleComplete(id: string) {
    try {
      await completeTask(id)
      toast.success('Task completed')
      onReload()
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to complete task'))
    }
  }

  return (
    <div className="page-enter flex flex-col gap-4">
      <SectionTitle
        title="Field Schedule"
        subtitle={tasks.length === 0 ? 'Field visits, demos and follow-ups, planned by day.' : taskCountLine(tasks.length)}
        action={action}
      />

      <div className="k-card">
        {tasks.length === 0 ? (
          <EmptyState
            message="Nothing scheduled"
            hint="Field tasks you schedule will appear here, grouped by day"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="closr-table ops-table w-full">
              <thead>
                <tr>
                  <th className="text-left">Task</th>
                  <th className="text-left">Assigned to</th>
                  <th className="text-left">Date</th>
                  <th className="text-left">Time</th>
                  <th className="text-left">Status</th>
                  <th className="text-left"></th>
                </tr>
              </thead>
              <tbody>
                {groups.map(g => (
                  [
                    <tr key={`day-${g.date}`}>
                      <td colSpan={6} className="text-12 font-semibold text-ink-muted pt-4">
                        {formatTaskDay(g.date)}
                      </td>
                    </tr>,
                    ...g.tasks.map(t => (
                      <tr key={t.id}>
                        <td>
                          <div className="text-13 font-medium text-ink-primary">{taskTypeLabel(t.taskType)}</div>
                          {t.notes && <div className="text-11 text-ink-muted mt-1" style={{ maxWidth: 380 }}>{t.notes}</div>}
                        </td>
                        <td className="text-12 text-ink-secondary">{t.assignedToName || '—'}</td>
                        <td className="text-12 text-ink-secondary whitespace-nowrap">{formatSopDate(t.scheduledDate)}</td>
                        <td className="text-12 text-ink-secondary whitespace-nowrap">{t.scheduledTime || '—'}</td>
                        <td><TaskStatusBadge status={t.status} /></td>
                        <td className="whitespace-nowrap">
                          <div className="flex items-center gap-2 justify-end">
                            {t.status === 'scheduled' && records && (
                              <button className="k-btn-secondary" onClick={() => handleComplete(t.id)}>
                                Complete
                              </button>
                            )}
                            {records && (
                              <button className="k-btn-secondary" onClick={() => onEdit(t)}>
                                Edit
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )),
                  ]
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main page: Calendar-primary with the Calendar/List toggle ─────────────────

export function OperationsTasks() {
  const [view, setView] = useState<'calendar' | 'list'>('calendar')
  const [reloadKey, setReloadKey] = useState(0)
  const [drawer, setDrawer] = useState<{ open: boolean; task: OpsTask | null }>({ open: false, task: null })
  const [detail, setDetail] = useState<OpsTask | null>(null)
  // New Task is a records-level write (lib/writeAccess; fail closed).
  const { records } = useWriteAccess()

  const reload = () => setReloadKey(k => k + 1)
  const openEdit = (task: OpsTask) => { setDetail(null); setDrawer({ open: true, task }) }

  // The toggle + New Task share the SectionTitle action slot in both views.
  const action = (
    <div className="flex items-center gap-2">
      {/* Two mutually exclusive views = the contract's segmented control, not
          two buttons.

          These stay role=button. An earlier pass made them role=tab, which is
          wrong twice over: a tab needs aria-controls and real tabpanels (there
          are none — the views swap in place), and it silently broke every
          getByRole('button', {name:'List'}) in the visual suite.

          aria-pressed carries the toggle semantics for assistive tech.
          aria-selected is ALSO set because klaxiora.css keys the active fill on
          `.k-segmented > [aria-selected="true"]`; both derive from the same
          boolean so they cannot diverge. Worth reporting upstream: the
          contract's styling hook is an attribute only valid on tab/option
          roles, so any real button-based segmented control has to carry it
          redundantly. */}
      <div className="k-segmented" role="group" aria-label="Schedule view">
        <button aria-pressed={view === 'calendar'} aria-selected={view === 'calendar'}
          onClick={() => setView('calendar')}>Calendar</button>
        <button aria-pressed={view === 'list'} aria-selected={view === 'list'}
          onClick={() => setView('list')}>List</button>
      </div>
      {records && (
        <button className="k-btn inline-flex items-center gap-2" onClick={() => setDrawer({ open: true, task: null })}>
          <Plus size={14} /> New Task
        </button>
      )}
    </div>
  )

  return (
    <>
      {view === 'calendar' ? (
        <CalendarView reloadKey={reloadKey} onReload={reload} action={action} onOpenDetail={setDetail} />
      ) : (
        <ListView reloadKey={reloadKey} onReload={reload} action={action} onEdit={openEdit} />
      )}

      {detail && (
        <TaskDetailModal
          task={detail}
          onClose={() => setDetail(null)}
          onEdit={openEdit}
          onChanged={reload}
        />
      )}

      {drawer.open && records && (
        <TaskDrawer
          task={drawer.task}
          onClose={() => setDrawer({ open: false, task: null })}
          onSaved={reload}
        />
      )}
    </>
  )
}
